export type SiteMapEmbedInput = Readonly<{
    address?: string | null;
    latitude?: number | string | null;
    longitude?: number | string | null;
    mapUrl?: string | null;
}>;

export type ShopMapEmbedInput = SiteMapEmbedInput;

export type SiteVideoSource = Readonly<{
    kind: "embed" | "file";
    source: string;
}>;

const iframeMarkupPattern = /^<iframe\b([\s\S]*?)>\s*<\/iframe\s*>$/i;
const selfClosingIframeMarkupPattern = /^<iframe\b([\s\S]*?)\/>$/i;
const quotedSourceAttributePattern = /(?:^|\s)src\s*=\s*(["'])([\s\S]*?)\1/i;
const youtubeVideoIdPattern = /^[A-Za-z0-9_-]{6,64}$/;
const directVideoPathPattern = /\.(?:m3u8|m4v|mov|mp4|og[gv]|webm)$/iu;

function iframeSourceCandidate(value: string): string | null {
    const trimmed = value.trim();

    if (trimmed === "") {
        return null;
    }

    if (!trimmed.startsWith("<")) {
        return trimmed;
    }

    const iframeMatch = trimmed.match(iframeMarkupPattern)
        ?? trimmed.match(selfClosingIframeMarkupPattern);

    if (!iframeMatch) {
        return null;
    }

    const sourceMatch = iframeMatch[1]?.match(quotedSourceAttributePattern);

    if (!sourceMatch?.[2]) {
        return null;
    }

    return sourceMatch[2]
        .replace(/&amp;/gi, "&")
        .replace(/&#0*38;/gi, "&")
        .replace(/&#x0*26;/gi, "&")
        .trim();
}

function httpUrl(value: string): URL | null {
    try {
        const url = new URL(value);

        if (
            (url.protocol !== "http:" && url.protocol !== "https:")
            || url.username !== ""
            || url.password !== ""
        ) {
            return null;
        }

        return url;
    } catch {
        return null;
    }
}

function hostnameMatches(hostname: string, domain: string): boolean {
    return hostname === domain || hostname.endsWith(`.${domain}`);
}

function youtubeStartSeconds(value: string | null): string | null {
    if (!value) {
        return null;
    }

    if (/^\d+$/.test(value)) {
        return value;
    }

    const duration = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i);

    if (!duration || duration.slice(1).every((part) => part === undefined)) {
        return null;
    }

    const seconds = Number(duration[1] ?? 0) * 3600
        + Number(duration[2] ?? 0) * 60
        + Number(duration[3] ?? 0);

    return String(seconds);
}

function normalizeYoutubeUrl(url: URL): string | null {
    const hostname = url.hostname.toLowerCase();
    const youtubeHost = hostnameMatches(hostname, "youtube.com")
        || hostnameMatches(hostname, "youtube-nocookie.com");
    const shortHost = hostnameMatches(hostname, "youtu.be");

    if (!youtubeHost && !shortHost) {
        return null;
    }

    const pathParts = url.pathname.split("/").filter(Boolean);
    let videoId = shortHost ? pathParts[0] : null;

    if (!videoId && url.pathname === "/watch") {
        videoId = url.searchParams.get("v");
    }

    if (!videoId && ["embed", "live", "shorts"].includes(pathParts[0] ?? "")) {
        videoId = pathParts[1] ?? null;
    }

    if (!videoId || !youtubeVideoIdPattern.test(videoId)) {
        return null;
    }

    const normalized = new URL(`https://www.youtube-nocookie.com/embed/${videoId}`);
    const allowedParameters = [
        "autoplay",
        "controls",
        "end",
        "index",
        "list",
        "loop",
        "mute",
        "playlist",
        "playsinline",
        "rel",
        "start",
    ];

    for (const parameter of allowedParameters) {
        const parameterValue = url.searchParams.get(parameter);

        if (parameterValue !== null) {
            normalized.searchParams.set(parameter, parameterValue);
        }
    }

    if (!normalized.searchParams.has("start")) {
        const start = youtubeStartSeconds(url.searchParams.get("t"));

        if (start !== null) {
            normalized.searchParams.set("start", start);
        }
    }

    return normalized.toString();
}

function normalizeVimeoUrl(url: URL): string | null {
    const hostname = url.hostname.toLowerCase();

    if (!hostnameMatches(hostname, "vimeo.com")) {
        return null;
    }

    const videoId = url.pathname
        .split("/")
        .filter(Boolean)
        .reverse()
        .find((part) => /^\d+$/.test(part));

    if (!videoId) {
        return null;
    }

    const normalized = new URL(`https://player.vimeo.com/video/${videoId}`);
    const allowedParameters = [
        "autoplay",
        "background",
        "byline",
        "dnt",
        "h",
        "loop",
        "muted",
        "portrait",
        "title",
    ];

    for (const parameter of allowedParameters) {
        const parameterValue = url.searchParams.get(parameter);

        if (parameterValue !== null) {
            normalized.searchParams.set(parameter, parameterValue);
        }
    }

    return normalized.toString();
}

function googleHostname(hostname: string): boolean {
    return hostname === "google.com"
        || hostname.endsWith(".google.com")
        || /^(?:[^.]+\.)*google\.[a-z]{2,3}(?:\.[a-z]{2})?$/.test(hostname);
}

function googleMapQueryUrl(query: string): string {
    const normalized = new URL("https://maps.google.com/maps");

    normalized.searchParams.set("q", query);
    normalized.searchParams.set("output", "embed");

    return normalized.toString();
}

function normalizedCoordinate(value: unknown, minimum: number, maximum: number): number | null {
    if ((typeof value !== "number" && typeof value !== "string") || String(value).trim() === "") {
        return null;
    }

    const coordinate = Number(value);

    return Number.isFinite(coordinate) && coordinate >= minimum && coordinate <= maximum
        ? coordinate
        : null;
}

function openStreetMapCoordinateUrl(latitude: number, longitude: number): string {
    const normalized = new URL("https://www.openstreetmap.org/export/embed.html");
    const bbox = [
        (longitude - 0.01).toFixed(6),
        (latitude - 0.006).toFixed(6),
        (longitude + 0.01).toFixed(6),
        (latitude + 0.006).toFixed(6),
    ].join(",");

    normalized.searchParams.set("bbox", bbox);
    normalized.searchParams.set("layer", "mapnik");
    normalized.searchParams.set("marker", `${latitude.toFixed(6)},${longitude.toFixed(6)}`);

    return normalized.toString();
}

function normalizeGoogleMapUrl(url: URL): string | null {
    const hostname = url.hostname.toLowerCase();

    if (hostname === "maps.app.goo.gl") {
        return null;
    }

    if (!googleHostname(hostname) || !url.pathname.toLowerCase().includes("/maps")) {
        return null;
    }

    const lowerPath = url.pathname.toLowerCase();

    if (
        lowerPath.includes("/maps/embed")
        || lowerPath.includes("/maps/d/embed")
        || url.searchParams.get("output") === "embed"
    ) {
        url.protocol = "https:";

        return url.toString();
    }

    const query = url.searchParams.get("q")
        ?? url.searchParams.get("query")
        ?? url.searchParams.get("destination")
        ?? url.searchParams.get("ll");

    if (query?.trim()) {
        return googleMapQueryUrl(query.trim());
    }

    const placeMatch = url.pathname.match(/\/maps\/place\/([^/]+)/i);

    if (placeMatch?.[1]) {
        try {
            return googleMapQueryUrl(decodeURIComponent(placeMatch[1].replace(/\+/g, " ")));
        } catch {
            return null;
        }
    }

    const coordinateMatch = url.pathname.match(/\/maps\/(?:place\/[^/]+\/)?@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/i);

    if (coordinateMatch?.[1] && coordinateMatch[2]) {
        const latitude = normalizedCoordinate(coordinateMatch[1], -90, 90);
        const longitude = normalizedCoordinate(coordinateMatch[2], -180, 180);

        return latitude !== null && longitude !== null
            ? googleMapQueryUrl(`${latitude},${longitude}`)
            : null;
    }

    return null;
}

function normalizeOpenStreetMapUrl(url: URL): string | null {
    if (!hostnameMatches(url.hostname.toLowerCase(), "openstreetmap.org")) {
        return null;
    }

    if (url.pathname.toLowerCase() === "/export/embed.html") {
        url.protocol = "https:";

        return url.toString();
    }

    const queryLatitude = normalizedCoordinate(url.searchParams.get("mlat"), -90, 90);
    const queryLongitude = normalizedCoordinate(url.searchParams.get("mlon"), -180, 180);

    if (queryLatitude !== null && queryLongitude !== null) {
        return openStreetMapCoordinateUrl(queryLatitude, queryLongitude);
    }

    const hashCoordinates = url.hash.match(/^#map=\d+(?:\.\d+)?\/(-?\d+(?:\.\d+)?)\/(-?\d+(?:\.\d+)?)/i);
    const hashLatitude = normalizedCoordinate(hashCoordinates?.[1], -90, 90);
    const hashLongitude = normalizedCoordinate(hashCoordinates?.[2], -180, 180);

    return hashLatitude !== null && hashLongitude !== null
        ? openStreetMapCoordinateUrl(hashLatitude, hashLongitude)
        : null;
}

/**
 * Resolves either a plain HTTP(S) URL or a single pasted iframe snippet to a safe iframe source.
 * Known video and map providers are converted to their embeddable URL forms. Arbitrary HTML is
 * deliberately rejected; consumers must never render the original markup as HTML.
 */
export function siteEmbedSource(value: string | null | undefined): string | null {
    if (typeof value !== "string") {
        return null;
    }

    const candidate = iframeSourceCandidate(value);
    const url = candidate ? httpUrl(candidate) : null;

    if (!url) {
        return null;
    }

    const hostname = url.hostname.toLowerCase();

    if (
        hostnameMatches(hostname, "youtube.com")
        || hostnameMatches(hostname, "youtube-nocookie.com")
        || hostnameMatches(hostname, "youtu.be")
    ) {
        return normalizeYoutubeUrl(url);
    }

    if (hostnameMatches(hostname, "vimeo.com")) {
        return normalizeVimeoUrl(url);
    }

    if (hostname === "maps.app.goo.gl" || googleHostname(hostname)) {
        return normalizeGoogleMapUrl(url);
    }

    if (hostnameMatches(hostname, "openstreetmap.org")) {
        return normalizeOpenStreetMapUrl(url);
    }

    return url.toString();
}

/** Resolves a safe video source and distinguishes native video files from iframe embeds. */
export function siteVideoSource(value: string | null | undefined): SiteVideoSource | null {
    const source = siteEmbedSource(value);

    if (source === null) {
        return null;
    }

    const url = httpUrl(source);

    return url && directVideoPathPattern.test(url.pathname)
        ? { kind: "file", source }
        : { kind: "embed", source };
}

/** Resolves an explicit map source first, then coordinates, and finally a textual address. */
export function siteMapEmbedSource(input: SiteMapEmbedInput): string | null {
    const explicitSource = siteEmbedSource(input.mapUrl);

    if (explicitSource !== null) {
        return explicitSource;
    }

    const latitude = normalizedCoordinate(input.latitude, -90, 90);
    const longitude = normalizedCoordinate(input.longitude, -180, 180);

    if (latitude !== null && longitude !== null) {
        return openStreetMapCoordinateUrl(latitude, longitude);
    }

    const address = typeof input.address === "string" ? input.address.trim() : "";

    return address === "" ? null : googleMapQueryUrl(address);
}

export const shopEmbedSource = siteEmbedSource;
export const shopMapEmbedSource = siteMapEmbedSource;
export const shopVideoSource = siteVideoSource;
